import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Stack } from '@mui/material';

import { currentSpaPath } from '@/shared/api';
import {
  resolvePasskeyEnrolmentOutcome,
  takePasskeyEnrolmentOutcome,
} from '@/shared/auth/passkeyEnrolment';
import type { PasskeyEnrolmentOutcome } from '@/shared/auth/passkeyEnrolment';
import { useSession } from '@/shared/session';
import { PageContainer, useDocumentTitle } from '@/shared/ui';

import { AccountConsoleLinksCard } from '../components/AccountConsoleLinksCard';
import { PasskeyCard } from '../components/PasskeyCard';
import { PasskeyOutcomeAlert } from '../components/PasskeyOutcomeAlert';

/**
 * `/account/security` — «Вход и безопасность» (user-story-merchant-portal-060,
 * adr-merchant-portal-020). An account-level page, deliberately outside `RequireSection` /
 * `RequirePermission` and not an `APP_SECTIONS` entry: credentials belong to the person, not the
 * tenant, so every signed-in employee reaches it — including one with no permission codes at all
 * (see `app/router/routeGuardCoverage.test.tsx`'s `UNGUARDED_BY_DESIGN` entry for this route).
 *
 * Composes the once-only outcome alert (this page is one of the two consumers of the enrolment
 * round trip — see `PasskeyEnrolmentReturn` for the other), the passkey card, and the static
 * Account Console links card. Fetches nothing itself.
 */
export function AccountSecurityPage() {
  const { t } = useTranslation('account');
  useDocumentTitle(t('security.title'));

  const session = useSession();
  const [outcome, setOutcome] = useState<PasskeyEnrolmentOutcome | null>(null);
  // Guards against StrictMode's double-invoked effects and against re-running once a marker has
  // already been consumed and turned into the outcome above — a reload must never repeat it.
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) {
      return;
    }
    const pending = takePasskeyEnrolmentOutcome(currentSpaPath());
    if (!pending) {
      return;
    }
    consumed.current = true;
    setOutcome(resolvePasskeyEnrolmentOutcome(pending, session.passkeyEnrolled));
  }, [session.passkeyEnrolled]);

  return (
    <PageContainer title={t('security.title')} subtitle={t('security.subtitle')} maxWidth={880}>
      <Stack spacing={2.5}>
        <PasskeyOutcomeAlert outcome={outcome} onClose={() => setOutcome(null)} />
        <PasskeyCard />
        <AccountConsoleLinksCard />
      </Stack>
    </PageContainer>
  );
}
